import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";

import { eventsApi } from "../../api";
import { rsvpApi } from "../../api/rsvp";
import { QueryErrorBanner, TableSkeletonRows } from "../../components/feedback/QueryFeedback";
import { APP_WORKSPACE } from "../../constants/appWorkspace";
import { absoluteUrl } from "../../config/site";
import { useAuth } from "../../context/AuthContext";
import { MobileSelect } from "../../components/ui/MobileSelect";
import { queryKeys } from "../../queryKeys";
import { appNav } from "../../routes/appNavigation";
import type { GuestChannel, RsvpGuest, RsvpGuestCreate, TicketTemplateId, TierCountMap } from "../../types/rsvp";
import {
  DEFAULT_TICKET_TEMPLATE_CONFIG,
  formatTierDistribution,
  isGuestListGuest,
  sumTierCounts,
} from "../../types/rsvp";
import { mergeTemplateConfig } from "../../utils/ticketTemplateStore";
import { EventPromoterPanel } from "../../components/ticketTemplate/EventPromoterPanel";
import { EventPromoterAccountsPanel } from "../../components/ticketTemplate/EventPromoterAccountsPanel";
import { isOwner } from "../../utils/roles";
import { TicketTemplateEditorModal } from "../../components/ticketTemplate/TicketTemplateEditorModal";
import { PaidFormsCard } from "./PaidFormsCard";
import "../../components/ticketTemplate/ticketTemplate.css";
import { toE164 } from "../../utils/phone";
import { formatINR } from "../../utils/format";
import { sortRsvpGuestsForTable } from "../../utils/eventRsvpGuestSort";
import { formatUtcToIstWall } from "../../utils/rsvpStore";
import "./EventRsvpTab.css";

type SortKey = "party_size" | "amount";
type SortDir = "asc" | "desc";
type LinkStatusTone = "active" | "scheduled" | "disabled";
type TableViewMode = "rsvp" | "guest_list";

function linkStatusMeta(
  disableAtUtc: string | null | undefined,
  disableAtIst: string | null | undefined,
): { label: string; tone: LinkStatusTone } {
  if (!disableAtUtc) return { label: "Active", tone: "active" };
  const t = new Date(disableAtUtc).getTime();
  if (Number.isNaN(t)) return { label: "Active", tone: "active" };
  if (Date.now() >= t) return { label: "Disabled", tone: "disabled" };
  const ist = disableAtIst || formatUtcToIstWall(disableAtUtc);
  return {
    label: ist ? `Until ${ist.replace("T", " ")} IST` : "Scheduled",
    tone: "scheduled",
  };
}

function normalizeIstDatetimeLocal(value: string | null | undefined): string {
  if (!value) return "";
  const trimmed = value.trim().replace(" ", "T");
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(trimmed)) return trimmed.slice(0, 16);
  const fromUtc = formatUtcToIstWall(trimmed);
  return fromUtc ? fromUtc.slice(0, 16) : "";
}

type ShareLinkCardProps = {
  title: string;
  url: string;
  disableIst: string;
  disableAtUtc: string | null | undefined;
  disableAtIst: string | null | undefined;
  saving: boolean;
  isManageActive?: boolean;
  onManage: () => void;
  onViewTemplate: () => void;
  onDisableIstChange: (value: string) => void;
  onSave: () => void;
  onClear: () => void;
  onCopy: () => void;
};

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  none: "—",
  pending: "Awaiting payment",
  paid: "Paid",
  due: "Pay at gate",
  refunded: "Refunded",
};

const ShareLinkCard: React.FC<ShareLinkCardProps> = ({
  title,
  url,
  disableIst,
  disableAtUtc,
  disableAtIst,
  saving,
  isManageActive = false,
  onManage,
  onViewTemplate,
  onDisableIstChange,
  onSave,
  onClear,
  onCopy,
}) => {
  const status = linkStatusMeta(disableAtUtc, disableAtIst);

  return (
    <article className={`event-rsvp__share-card${isManageActive ? " event-rsvp__share-card--active" : ""}`}>
      <div className="event-rsvp__share-card-head">
        <h3 className="event-rsvp__share-card-title">{title}</h3>
        <span className={`event-rsvp__status-pill event-rsvp__status-pill--${status.tone}`}>{status.label}</span>
      </div>
      <div className="event-rsvp__url-row">
        <input
          className="eventos-input event-rsvp__share-url"
          readOnly
          value={url}
          aria-label={`${title} public link`}
          onFocus={(e) => e.currentTarget.select()}
        />
        <button type="button" className="eventos-nav-btn event-rsvp__copy-btn" onClick={onCopy} disabled={!url}>
          Copy
        </button>
      </div>
      <label className="event-rsvp__disable-field">
        <span>Auto-disable (IST)</span>
        <input
          className="eventos-input"
          type="datetime-local"
          value={disableIst}
          onChange={(e) => onDisableIstChange(e.target.value)}
          aria-label={`${title} link disable time in IST`}
        />
      </label>
      <div className="event-rsvp__share-card-actions">
        <button
          type="button"
          className={`eventos-nav-btn event-rsvp__share-card-btn${isManageActive ? " is-active" : ""}`}
          onClick={onManage}
        >
          Manage
        </button>
        <button type="button" className="eventos-nav-btn event-rsvp__share-card-btn" onClick={onViewTemplate}>
          View Template
        </button>
        <button type="button" className="eventos-nav-btn event-rsvp__share-card-btn" disabled={saving} onClick={onSave}>
          Save
        </button>
        <button
          type="button"
          className="eventos-nav-btn event-rsvp__share-card-btn"
          disabled={saving || !disableAtUtc}
          onClick={onClear}
        >
          Clear
        </button>
      </div>
    </article>
  );
};

const emptyForm = (tierIds: string[]): RsvpGuestCreate => ({
  name: "",
  phone: "",
  party_size: 1,
  category: "stag",
  tier_counts: Object.fromEntries(tierIds.map((id) => [id, 0])),
  amount: null,
  source: "manual",
  channel: "rsvp",
  whatsapp_opt_in: false,
});

function tierInputValue(counts: TierCountMap | undefined, tierId: string): string {
  const n = counts?.[tierId];
  if (n == null || n === 0) return "";
  return String(n);
}

const EventRsvpTab: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { token, user } = useAuth();
  const queryClient = useQueryClient();
  const workspace = APP_WORKSPACE;

  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("party_size");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [form, setForm] = useState<RsvpGuestCreate>(() => emptyForm([]));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [rsvpDisableIst, setRsvpDisableIst] = useState("");
  const [guestListDisableIst, setGuestListDisableIst] = useState("");
  const [manualEntryExpanded, setManualEntryExpanded] = useState(false);
  const [tableView, setTableView] = useState<TableViewMode>("rsvp");
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templatePreviewKind, setTemplatePreviewKind] = useState<TableViewMode>("rsvp");

  const eventQuery = useQuery({
    queryKey: eventId && token ? queryKeys.events.detail(workspace, eventId) : queryKeys.events.all(workspace),
    queryFn: () => eventsApi.detail(eventId!, token!),
    enabled: Boolean(token && eventId),
  });

  const settingsQuery = useQuery({
    queryKey: eventId ? queryKeys.rsvpSettings(workspace, eventId) : ["rsvp", "idle"],
    queryFn: () => rsvpApi.getSettings(eventId!, token!),
    enabled: Boolean(eventId),
  });

  const guestsQuery = useQuery({
    queryKey: eventId ? queryKeys.rsvpGuests(workspace, eventId) : ["rsvp", "idle-guests"],
    queryFn: () => rsvpApi.listGuests(eventId!, token!),
    enabled: Boolean(eventId),
  });

  const promotersQuery = useQuery({
    queryKey: eventId ? queryKeys.eventPromoters(workspace, eventId) : ["promoters", "idle"],
    queryFn: () => rsvpApi.listPromoters(eventId!, token ?? undefined),
    enabled: Boolean(eventId),
  });

  const tiers = useMemo(() => {
    const fromEvent = eventQuery.data?.ticket_tiers ?? [];
    return fromEvent.map((t: { id: string; tier_name: string; price: number }) => ({
      id: t.id,
      name: t.tier_name,
      price: t.price,
    }));
  }, [eventQuery.data]);

  const tierNames = useMemo(
    () => Object.fromEntries(tiers.map((t: { id: string; name: string }) => [t.id, t.name])),
    [tiers],
  );

  useEffect(() => {
    if (!settingsQuery.data) return;
    setRsvpDisableIst(normalizeIstDatetimeLocal(settingsQuery.data.rsvp_disable_at_ist));
    setGuestListDisableIst(normalizeIstDatetimeLocal(settingsQuery.data.guest_list_disable_at_ist));
  }, [settingsQuery.data]);

  useEffect(() => {
    if (!eventId || !eventQuery.data) return;
    rsvpApi.rememberEventMeta(eventId, {
      name: eventQuery.data.name,
      event_date: eventQuery.data.event_date,
      city: eventQuery.data.city,
      artist: eventQuery.data.artist,
      ticket_tiers: tiers,
    });
  }, [eventId, eventQuery.data, tiers]);

  useEffect(() => {
    if (editingId) return;
    setForm((prev) => ({
      ...prev,
      tier_counts: Object.fromEntries(
        tiers.map((t: { id: string }) => [t.id, prev.tier_counts?.[t.id] ?? 0]),
      ),
    }));
  }, [tiers, editingId]);

  const guests = useMemo<RsvpGuest[]>(() => guestsQuery.data ?? [], [guestsQuery.data]);
  const inviteUrl = settingsQuery.data?.invite_token
    ? absoluteUrl(appNav.publicRsvp(settingsQuery.data.invite_token))
    : "";
  const guestListUrl = settingsQuery.data?.guest_list_token
    ? absoluteUrl(appNav.publicGuestList(settingsQuery.data.guest_list_token))
    : "";
  const walkInUrl = settingsQuery.data?.walk_in_token
    ? absoluteUrl(appNav.publicWalkIn(settingsQuery.data.walk_in_token))
    : "";

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = guests.filter((g) =>
      tableView === "guest_list" ? isGuestListGuest(g) : !isGuestListGuest(g),
    );
    if (q) {
      rows = rows.filter(
        (g) =>
          g.name.toLowerCase().includes(q) ||
          g.phone.toLowerCase().includes(q) ||
          (g.promoter ?? "").toLowerCase().includes(q),
      );
    }
    rows = sortRsvpGuestsForTable(rows, tableView, sortKey, sortDir);
    return rows;
  }, [guests, search, sortKey, sortDir, tableView]);

  const invalidate = async () => {
    if (!eventId) return;
    await queryClient.invalidateQueries({ queryKey: queryKeys.rsvpGuests(workspace, eventId) });
    await queryClient.invalidateQueries({ queryKey: queryKeys.rsvpSettings(workspace, eventId) });
  };

  const buildPayload = (): RsvpGuestCreate => {
    const tier_counts: TierCountMap = { ...(form.tier_counts ?? {}) };
    const party_size = sumTierCounts(tier_counts) || Math.max(1, Number(form.party_size) || 1);
    return {
      name: form.name.trim(),
      phone: toE164(form.phone) ?? form.phone.trim(),
      party_size,
      category: form.category ?? "stag",
      tier_counts,
      amount: form.amount == null ? null : Number(form.amount),
      source: form.source ?? (editingId ? undefined : "manual"),
      channel: form.channel ?? "rsvp",
      promoter: form.promoter?.trim() || null,
      promoter_id: form.promoter_id ?? null,
      approved:
        form.channel === "guest_list" || form.source === "guest_list"
          ? form.approved ?? true
          : true,
      whatsapp_opt_in: form.whatsapp_opt_in ?? false,
    };
  };

  const approvalMutation = useMutation({
    mutationFn: async ({ guestId, approved }: { guestId: string; approved: boolean }) => {
      if (!eventId) throw new Error("Missing event");
      return rsvpApi.updateGuest(eventId, guestId, { approved }, token!);
    },
    onSuccess: async () => {
      await invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not update approval"),
  });

  const undoCheckInMutation = useMutation({
    mutationFn: async (guestId: string) => {
      if (!eventId) throw new Error("Missing event");
      return rsvpApi.undoCheckInGuest(eventId, guestId, token!);
    },
    onSuccess: async () => {
      toast.success("Check-in undone");
      await invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not undo check-in"),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!eventId) throw new Error("Missing event");
      const payload = buildPayload();
      if (editingId) return rsvpApi.updateGuest(eventId, editingId, payload, token!);
      return rsvpApi.createGuest(eventId, payload, token!);
    },
    onSuccess: async () => {
      toast.success(editingId ? "Guest updated" : "Guest added");
      setForm(emptyForm(tiers.map((t: { id: string }) => t.id)));
      setEditingId(null);
      await invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Save failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (guestId: string) => {
      if (!eventId) throw new Error("Missing event");
      await rsvpApi.deleteGuest(eventId, guestId, token!);
    },
    onSuccess: async () => {
      toast.success("Guest removed");
      setEditingId(null);
      setForm(emptyForm(tiers.map((t: { id: string }) => t.id)));
      await invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Delete failed"),
  });

  const saveDisableMutation = useMutation({
    mutationFn: async (payload: {
      rsvp_disable_at_ist?: string | null;
      guest_list_disable_at_ist?: string | null;
    }) => {
      if (!eventId) throw new Error("Missing event");
      return rsvpApi.updateSettings(eventId, payload, token!);
    },
    onSuccess: async () => {
      toast.success("Link schedule saved");
      await invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not save schedule"),
  });

  const startEdit = (guest: RsvpGuest) => {
    setEditingId(guest.id);
    setManualEntryExpanded(true);
    const tier_counts: TierCountMap = Object.fromEntries(
      tiers.map((t: { id: string }) => [t.id, guest.tier_counts?.[t.id] ?? 0]),
    );
    if (Object.keys(guest.tier_counts ?? {}).length === 0 && guest.party_size) {
      if (tiers[0]) tier_counts[tiers[0].id] = guest.party_size;
    }
    setForm({
      name: guest.name,
      phone: guest.phone,
      party_size: guest.party_size,
      category: guest.category,
      tier_counts,
      amount: guest.amount,
      promoter: guest.promoter ?? "",
      source: guest.source ?? "manual",
      channel: guest.channel ?? "rsvp",
      approved: guest.approved ?? true,
      whatsapp_opt_in: guest.whatsapp_opt_in ?? false,
    });
  };

  const templateConfig = mergeTemplateConfig(
    settingsQuery.data?.template_config ?? DEFAULT_TICKET_TEMPLATE_CONFIG,
  );
  const resolvedTemplateId: TicketTemplateId =
    settingsQuery.data?.resolved_ticket_template_id ??
    settingsQuery.data?.ticket_template_id ??
    "black";

  const previewMeta = {
    event_name: eventQuery.data?.name ?? settingsQuery.data?.event_name ?? "Event",
    event_date: eventQuery.data?.event_date ?? settingsQuery.data?.event_date ?? "",
    city: eventQuery.data?.city ?? settingsQuery.data?.city ?? "",
    artist: eventQuery.data?.artist ?? settingsQuery.data?.artist ?? null,
    link_kind: templatePreviewKind,
    banner_url: templateConfig.banner_url ?? null,
    lineup: eventQuery.data?.artist
      ? [{ name: eventQuery.data.artist, role: "headliner" as const }]
      : [],
  };

  const previewPromoters = (promotersQuery.data ?? []).map((p: { id: string; name: string }) => ({
    id: p.id,
    name: p.name,
  }));

  const openTemplateModal = (kind: TableViewMode) => {
    setTemplatePreviewKind(kind);
    setTemplateModalOpen(true);
  };

  const copyLink = async (url: string, label: string) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const setTierCount = (tierId: string, value: number) => {
    setForm((p) => {
      const tier_counts = { ...(p.tier_counts ?? {}), [tierId]: Math.max(0, value) };
      return { ...p, tier_counts, party_size: sumTierCounts(tier_counts) || 1 };
    });
  };

  if (!eventId) return <div className="eventos-empty">Open an event to manage RSVPs.</div>;

  return (
    <div className="event-rsvp">
      <div className="event-rsvp__layout">
        <section className="event-rsvp__main" aria-label={tableView === "guest_list" ? "Guest List" : "RSVP guests"}>
          <div className="event-rsvp__main-head">
            <h2 className="event-rsvp__main-title">
              {tableView === "guest_list" ? "Guest List" : "RSVP"}
            </h2>
            <p className="eventos-subtle event-rsvp__main-hint">
              {tableView === "guest_list"
                ? "Approved guests move to the bottom and appear in Live Ops."
                : "Guests whose gate entry has been confirmed in Live Ops."}
            </p>
          </div>

          {tableView === "guest_list" && eventId ? (
            <EventPromoterPanel eventId={eventId} token={token ?? undefined} />
          ) : null}

          {eventId && isOwner(user) ? (
            <EventPromoterAccountsPanel eventId={eventId} token={token ?? undefined} />
          ) : null}

          {tableView === "rsvp" ? (
          <div className="event-rsvp__manual-panel">
            <div className="event-rsvp__manual-head">
              <div>
                <h2 className="event-rsvp__manual-title">
                  {editingId ? "Edit guest" : "Manual entry"}
                </h2>
                <p className="eventos-subtle event-rsvp__manual-hint">
                  Add guests without the public form
                </p>
              </div>
              <button
                type="button"
                className="eventos-nav-btn"
                aria-expanded={manualEntryExpanded}
                onClick={() => setManualEntryExpanded((prev) => !prev)}
              >
                {manualEntryExpanded ? "Collapse" : "Expand"}
              </button>
            </div>

            {manualEntryExpanded ? (
              <form
                className="event-rsvp__edit-bar event-rsvp__edit-bar--manual"
                onSubmit={(e) => {
                  e.preventDefault();
                  saveMutation.mutate();
                }}
              >
                <div className="event-rsvp__name-phone">
                  <input
                    className="eventos-input"
                    required
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    aria-label="Name"
                    placeholder="Name *"
                  />
                  <input
                    className="eventos-input"
                    required
                    inputMode="tel"
                    value={form.phone}
                    onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                    aria-label="Number"
                    placeholder="+91 98765 43210"
                  />
                </div>
                <label className="event-rsvp__checkbox">
                  <input
                    type="checkbox"
                    checked={form.whatsapp_opt_in ?? false}
                    onChange={(e) => setForm((p) => ({ ...p, whatsapp_opt_in: e.target.checked }))}
                  />
                  <span>Guest has agreed to receive WhatsApp messages</span>
                </label>
                <label className="event-rsvp__tier-field">
                  <span>Add to</span>
                  <MobileSelect
                    label="Add to RSVP or Guest List"
                    className="eventos-input"
                    value={form.channel ?? "rsvp"}
                    onChange={(v) =>
                      setForm((p) => ({
                        ...p,
                        source: "manual",
                        channel: v as GuestChannel,
                      }))
                    }
                    options={[
                      { value: "rsvp", label: "RSVP" },
                      { value: "guest_list", label: "Guest List" },
                    ]}
                  />
                </label>
                <div className="event-rsvp__tier-counts">
                  {tiers.length > 0 ? (
                    tiers.map((tier: { id: string; name: string }) => (
                      <label key={tier.id} className="event-rsvp__tier-field">
                        <span>{tier.name}</span>
                        <input
                          className="eventos-input"
                          type="number"
                          min={0}
                          value={tierInputValue(form.tier_counts, tier.id)}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setTierCount(tier.id, raw === "" ? 0 : Number(raw));
                          }}
                        />
                      </label>
                    ))
                  ) : (
                    <label className="event-rsvp__tier-field">
                      <span>Party size</span>
                      <input
                        className="eventos-input"
                        type="number"
                        min={1}
                        required
                        value={form.party_size}
                        onChange={(e) => setForm((p) => ({ ...p, party_size: Number(e.target.value) }))}
                      />
                    </label>
                  )}
                </div>
                <div className="event-rsvp__amount-row">
                  <label className="event-rsvp__tier-field">
                    <span>Amount</span>
                    <input
                      className="eventos-input"
                      type="number"
                      min={0}
                      step={1}
                      value={form.amount ?? ""}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          amount: e.target.value === "" ? null : Number(e.target.value),
                        }))
                      }
                    />
                  </label>
                  <div className="event-rsvp__tier-actions">
                    <button
                      type="submit"
                      className="eventos-primary-btn event-rsvp__add-guest-btn"
                      disabled={saveMutation.isPending}
                    >
                      {editingId ? "Save" : "Add guest"}
                    </button>
                  </div>
                </div>
                {editingId ? (
                  <div className="event-rsvp__edit-actions">
                    <button
                      type="button"
                      className="eventos-nav-btn eventos-nav-btn--danger"
                      onClick={() => {
                        if (window.confirm("Delete this guest?")) deleteMutation.mutate(editingId);
                      }}
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      className="eventos-nav-btn"
                      onClick={() => {
                        setEditingId(null);
                        setForm(emptyForm(tiers.map((t: { id: string }) => t.id)));
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : null}
              </form>
            ) : null}
          </div>
          ) : null}

          <div className="event-rsvp__toolbar">
            <label className="event-rsvp__search">
              <span className="visually-hidden">Search name or number</span>
              <input
                className="eventos-input"
                placeholder="Search name, number, promoter…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            <div className="event-rsvp__sorts">
              <button type="button" className="eventos-nav-btn" onClick={() => toggleSort("party_size")}>
                Party size {sortKey === "party_size" ? (sortDir === "asc" ? "↑" : "↓") : ""}
              </button>
              <button type="button" className="eventos-nav-btn" onClick={() => toggleSort("amount")}>
                Amount {sortKey === "amount" ? (sortDir === "asc" ? "↑" : "↓") : ""}
              </button>
            </div>
          </div>

          {guestsQuery.isError ? (
            <QueryErrorBanner
              message={guestsQuery.error instanceof Error ? guestsQuery.error.message : "Failed to load guests"}
              onRetry={() => void guestsQuery.refetch()}
            />
          ) : null}

          <div className="event-rsvp__table-wrap">
            <table className="app-shell-table event-rsvp__table">
              <thead>
                <tr>
                  <th>Name*</th>
                  <th>Number*</th>
                  <th>
                    <button type="button" className="event-rsvp__th-btn" onClick={() => toggleSort("party_size")}>
                      Party Size* {sortKey === "party_size" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                    </button>
                  </th>
                  {tableView === "guest_list" ? (
                    <>
                      <th>Promoter</th>
                      <th>Email</th>
                    </>
                  ) : (
                    <>
                      <th>Tier distribution</th>
                      <th>Promoter</th>
                    </>
                  )}
                  <th>
                    <button type="button" className="event-rsvp__th-btn" onClick={() => toggleSort("amount")}>
                      Amount {sortKey === "amount" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                    </button>
                  </th>
                  {tableView === "guest_list" ? <th>Approval</th> : null}
                  {tableView !== "guest_list" ? (
                    <>
                      <th>Payment</th>
                    </>
                  ) : null}
                  <th>Event entry time</th>
                  <th className="event-rsvp__actions-head">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {guestsQuery.isPending ? (
                  <TableSkeletonRows rows={6} cols={tableView === "guest_list" ? 9 : 10} />
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={tableView === "guest_list" ? 9 : 10} className="event-rsvp__empty">
                      {tableView === "guest_list"
                        ? "No Guest List entries yet. Share the Guest List link or approve pending signups."
                        : "No confirmed RSVP entries yet. Guests appear here once confirmed in Live Ops."}
                    </td>
                  </tr>
                ) : (
                  filtered.map((guest) => (
                    <tr
                      key={guest.id}
                      className={`${editingId === guest.id ? "is-editing" : ""}${guest.approved && tableView === "guest_list" ? " is-approved" : ""}`}
                    >
                      <td>{guest.name}</td>
                      <td className="eventos-table__num">{guest.phone}</td>
                      <td className="eventos-table__num">
                        {sumTierCounts(guest.tier_counts) || guest.party_size}
                      </td>
                      {tableView === "guest_list" ? (
                        <>
                          <td>{guest.promoter ?? "—"}</td>
                          <td>{guest.email ?? "—"}</td>
                        </>
                      ) : (
                        <>
                          <td>{formatTierDistribution(guest.tier_counts, tierNames)}</td>
                          <td>{guest.promoter ?? "—"}</td>
                        </>
                      )}
                      <td className="eventos-table__num">
                        {guest.amount != null ? formatINR(guest.amount) : "—"}
                      </td>
                      {tableView === "guest_list" ? (
                        <td>
                          <label className="event-rsvp__approval-toggle">
                            <input
                              type="checkbox"
                              checked={guest.approved === true}
                              disabled={approvalMutation.isPending}
                              onChange={(e) =>
                                approvalMutation.mutate({
                                  guestId: guest.id,
                                  approved: e.target.checked,
                                })
                              }
                            />
                            <span>{guest.approved ? "Approved" : "Pending"}</span>
                          </label>
                        </td>
                      ) : null}
                      {tableView !== "guest_list" ? (
                        <>
                          <td>{PAYMENT_STATUS_LABEL[guest.payment_status ?? "none"]}</td>
                        </>
                      ) : null}
                      <td>
                        {guest.checked_in_at ? (
                          <span className="event-rsvp__checkin-cell">
                            {formatUtcToIstWall(guest.checked_in_at)?.replace("T", " ") ?? "—"}
                            <button
                              type="button"
                              className="eventos-nav-btn"
                              disabled={undoCheckInMutation.isPending}
                              onClick={() => undoCheckInMutation.mutate(guest.id)}
                            >
                              Undo
                            </button>
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <button type="button" className="eventos-nav-btn" onClick={() => startEdit(guest)}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="event-rsvp__share-rail" aria-label="RSVP and Guest List links">
          <div className="event-rsvp__share-intro">
            <h2 className="event-rsvp__share-heading">Share</h2>
            <p className="event-rsvp__share-lede">
              Use Manage on each card to switch tables. View Template edits the public signup layout.
            </p>
          </div>

          <ShareLinkCard
            title="RSVP"
            url={inviteUrl}
            isManageActive={tableView === "rsvp"}
            onManage={() => setTableView("rsvp")}
            onViewTemplate={() => openTemplateModal("rsvp")}
            disableIst={rsvpDisableIst}
            disableAtUtc={settingsQuery.data?.rsvp_disable_at}
            disableAtIst={settingsQuery.data?.rsvp_disable_at_ist}
            saving={saveDisableMutation.isPending}
            onDisableIstChange={setRsvpDisableIst}
            onSave={() =>
              saveDisableMutation.mutate({
                rsvp_disable_at_ist: rsvpDisableIst.trim() || null,
              })
            }
            onClear={() => {
              setRsvpDisableIst("");
              saveDisableMutation.mutate({ rsvp_disable_at_ist: null });
            }}
            onCopy={() => void copyLink(inviteUrl, "RSVP link")}
          />

          <ShareLinkCard
            title="Guest List"
            url={guestListUrl}
            isManageActive={tableView === "guest_list"}
            onManage={() => setTableView("guest_list")}
            onViewTemplate={() => openTemplateModal("guest_list")}
            disableIst={guestListDisableIst}
            disableAtUtc={settingsQuery.data?.guest_list_disable_at}
            disableAtIst={settingsQuery.data?.guest_list_disable_at_ist}
            saving={saveDisableMutation.isPending}
            onDisableIstChange={setGuestListDisableIst}
            onSave={() =>
              saveDisableMutation.mutate({
                guest_list_disable_at_ist: guestListDisableIst.trim() || null,
              })
            }
            onClear={() => {
              setGuestListDisableIst("");
              saveDisableMutation.mutate({ guest_list_disable_at_ist: null });
            }}
            onCopy={() => void copyLink(guestListUrl, "Guest List link")}
          />
          <PaidFormsCard
            eventId={eventId!}
            token={token!}
            settings={settingsQuery.data}
            walkInUrl={walkInUrl}
            canProvision={isOwner(user)}
            onChanged={invalidate}
          />
        </aside>
      </div>

      <TicketTemplateEditorModal
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        eventId={eventId}
        initialTemplateId={resolvedTemplateId}
        initialConfig={templateConfig}
        previewMeta={previewMeta}
        initialPreviewKind={templatePreviewKind}
        previewPromoters={previewPromoters}
        onSave={async (payload) => {
          if (!eventId) return;
          await rsvpApi.updateSettings(
            eventId,
            {
              ticket_template_id: payload.ticket_template_id,
              template_config: payload.template_config,
            },
            token!,
          );
          await invalidate();
        }}
      />
    </div>
  );
};

export default EventRsvpTab;
